import React, { useState } from "react";
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Input,
} from "reactstrap";
import PropTypes from "prop-types";

export const AccordionComponent = ({
  items = [],
  className = "",
  allowMultiple = false,
  defaultOpenItems = [],
}) => {
  // Initialize with defaultOpenItems and items that have opened: true
  const getInitialOpenItems = () => {
    const alwaysOpenItems = items
      .filter((item) => item.opened)
      .map((item) => item?.id);

    return [...new Set([...defaultOpenItems, ...alwaysOpenItems])];
  };

  const [openItems, setOpenItems] = useState(getInitialOpenItems());

  const toggle = (id) => {
    // Find the item to check if it has opened prop
    const item = items.find((item) => item?.id === id);

    // If item has opened: true, don't allow it to be closed
    if (item?.opened) {
      return;
    }

    if (allowMultiple) {
      setOpenItems((prev) =>
        prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
      );
    } else {
      // Get items that should always stay open
      const alwaysOpenItems = items
        .filter((item) => item.opened)
        .map((item) => item?.id);

      // In single mode, close others but keep always-open items
      setOpenItems((prev) => {
        if (prev.includes(id)) {
          // If clicking on open item, close it but keep always-open items
          return alwaysOpenItems;
        } else {
          // If clicking on closed item, open it and keep always-open items
          return [...alwaysOpenItems, id];
        }
      });
    }
  };

  const renderHeaderContent = (item) => {
    const renderIcon = () => {
      if (!item.icon) return null;
      return typeof item.icon === "string" ? (
        <i className={item.icon}></i>
      ) : (
        item.icon
      );
    };

    const renderCheckbox = () => {
      if (!item.showCheckbox) return null;
      return (
        <Input
          type="checkbox"
          checked={item.checked || false}
          disabled={item.checkboxDisabled}
          onChange={(e) => {
            e.stopPropagation();
            if (item.onCheckboxChange) {
              item.onCheckboxChange(item?.id, e.target.checked);
            }
          }}
          onClick={(e) => e.stopPropagation()}
        />
      );
    };

    return (
      <div className="d-flex align-items-center gap-2">
        {renderCheckbox()}
        {renderIcon()}
        <span>{item.title}</span>
      </div>
    );
  };

  const renderBodyContent = (item) => {
    if (typeof item.content === "string") {
      return <div dangerouslySetInnerHTML={{ __html: item.content }} />;
    }
    return item.content;
  };

  if (!items || items.length === 0) {
    return null;
  }

  return (
    <Accordion open={openItems} toggle={toggle} className={className}>
      {items.map((item) => (
        <AccordionItem className={item.className || undefined} key={item?.id}>
          <AccordionHeader
            targetId={item?.id}
            className={item.headerClassName || undefined}
          >
            {renderHeaderContent(item)}
          </AccordionHeader>
          <AccordionBody
            accordionId={item?.id}
            className={item.bodyClassName || undefined}
          >
            {renderBodyContent(item)}
          </AccordionBody>
        </AccordionItem>
      ))}
    </Accordion>
  );
};

AccordionComponent.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
      content: PropTypes.oneOfType([PropTypes.string, PropTypes.node])
        .isRequired,
      icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
      showCheckbox: PropTypes.bool,
      checked: PropTypes.bool,
      checkboxDisabled: PropTypes.bool,
      onCheckboxChange: PropTypes.func,
      opened: PropTypes.bool,
      headerClassName: PropTypes.string,
      bodyClassName: PropTypes.string,
    })
  ).isRequired,
  className: PropTypes.string,
  allowMultiple: PropTypes.bool,
  defaultOpenItems: PropTypes.arrayOf(PropTypes.string),
};

AccordionComponent.defaultProps = {
  items: [],
  className: "",
  allowMultiple: false,
  defaultOpenItems: [],
};
